import React, { useState, useEffect } from 'react';
import { Filter, X } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import ProductCard from '../components/products/ProductCard';
import { useLocation } from 'react-router-dom';

const Shop = () => {
  const { products, categories, fetchProducts, loading } = useApp();
  const location = useLocation();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      fetchProducts({ category: categoryParam });
    } else {
      fetchProducts();
    }
  }, [location.search]);

  useEffect(() => {
    const filters = {};
    if (selectedCategory) filters.category = selectedCategory;
    fetchProducts(filters);
  }, [selectedCategory]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
  };

  const clearFilters = () => { setSelectedCategory(''); };
  const hasActiveFilters = !!selectedCategory;

  return (
    <div className="min-h-screen bg-[#FEFCF9]">
      {/* Header Banner */}
      <div className="relative py-14 sm:py-18 lg:py-24 overflow-hidden bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] text-[#C4A265] uppercase font-medium mb-3 sm:mb-4">Behat House Atelier</p>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-serif text-[#3E2723] mb-3 sm:mb-4">Our Collection</h1>
          <div className="w-10 sm:w-12 h-px bg-[#C4A265] mb-4 sm:mb-6"></div>
          <p className="text-[#7A6B5D] max-w-xl text-sm sm:text-base lg:text-lg">
            Discover handcrafted masterpieces that bring timeless elegance to your home
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Filters Sidebar — Product Type only */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-[#FEFCF9] p-8 sticky top-24 border-l-2 border-[#C4A265]">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-sm font-semibold text-[#3E2723] tracking-[0.2em] uppercase">Refine By</h3>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-[#C4A265] hover:text-[#A0822E] font-medium">Clear All</button>
                )}
              </div>
              <div>
                <h4 className="font-semibold text-[#3E2723] mb-4 text-xs tracking-[0.2em] uppercase">Product Type</h4>
                <div className="space-y-3">
                  {categories.map((category) => (
                    <label key={category.id} className="flex items-center space-x-3 cursor-pointer group">
                      <input type="checkbox" checked={selectedCategory === category.name} onChange={() => handleCategoryChange(category.name)} className="w-4 h-4 border-2 border-[#E8DCC8] text-[#C4A265] focus:ring-[#C4A265] rounded" />
                      <span className="text-sm text-[#7A6B5D] group-hover:text-[#3E2723] transition-colors">{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Filter Button */}
          <div className="lg:hidden">
            <button onClick={() => setShowFilters(true)} className="flex items-center space-x-2 bg-[#3E2723] text-[#FEFCF9] px-5 py-2.5 text-sm tracking-wider uppercase" data-testid="mobile-filter-button">
              <Filter className="w-4 h-4" /><span>Filters</span>
              {hasActiveFilters && <span className="bg-[#C4A265] text-white rounded-full px-2 py-0.5 text-xs font-semibold">Active</span>}
            </button>
          </div>

          {/* Mobile Filter Modal */}
          {showFilters && (
            <div className="fixed inset-0 bg-black/50 z-50 lg:hidden">
              <div className="fixed inset-y-0 left-0 w-80 bg-[#FEFCF9] p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-sm font-semibold text-[#3E2723] tracking-[0.2em] uppercase">Filters</h3>
                  <button onClick={() => setShowFilters(false)}><X className="w-6 h-6 text-[#7A6B5D]" /></button>
                </div>
                {hasActiveFilters && (
                  <button onClick={() => { clearFilters(); setShowFilters(false); }} className="w-full mb-6 text-sm text-[#C4A265] text-center">Clear All Filters</button>
                )}
                <div>
                  <h4 className="font-medium text-[#3E2723] mb-3 text-xs tracking-[0.2em] uppercase">Product Type</h4>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <label key={category.id} className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={selectedCategory === category.name} onChange={() => handleCategoryChange(category.name)} className="rounded text-[#C4A265] focus:ring-[#C4A265]" />
                        <span className="text-sm text-[#7A6B5D]">{category.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={() => setShowFilters(false)} className="w-full mt-6 bg-[#3E2723] text-[#FEFCF9] py-3 font-semibold tracking-wider uppercase text-sm">Apply Filters</button>
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {hasActiveFilters && (
              <div className="mb-6 flex flex-wrap items-center gap-2">
                <span className="text-sm text-[#7A6B5D]">Active filters:</span>
                <span className="inline-flex items-center space-x-1 bg-[#3E2723] text-[#FEFCF9] px-3 py-1 text-sm">
                  <span>{selectedCategory}</span>
                  <button onClick={() => setSelectedCategory('')}><X className="w-4 h-4" /></button>
                </span>
              </div>
            )}

            <div className="flex items-center justify-between mb-8 pb-4 border-b border-[#E8DCC8]">
              <p className="text-sm text-[#7A6B5D]"><span className="font-semibold text-[#3E2723]">{products.length}</span> {products.length === 1 ? 'Product' : 'Products'} Found</p>
              <div className="text-xs tracking-[0.2em] text-[#C4A265] uppercase font-medium">Handcrafted with Love</div>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block w-10 h-10 border-2 border-[#E8DCC8] border-t-[#C4A265] rounded-full animate-spin mb-4"></div>
                <p className="text-[#7A6B5D]">Curating products...</p>
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {products.map((product) => (<ProductCard key={product.id} product={product} />))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-[#3E2723] text-xl mb-4 font-serif">No products found</p>
                <p className="text-[#7A6B5D] mb-6">Try adjusting your filters</p>
                <button onClick={clearFilters} className="text-[#C4A265] font-semibold hover:text-[#A0822E] tracking-wider uppercase text-sm">Clear All Filters</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
